import React, { useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { applyColorClassToSelection } from '../utils/richTextSanitizer';

const BIDI_CONTROL = /[\u202A-\u202E\u2066-\u2069]/g;

const stripBidiControlChars = (value: string): string => value.replace(BIDI_CONTROL, '');

const COLORS = [
  '#FFFFFF',
  '#000000',
  '#333333',
  '#666666',
  '#999999',
  '#FF0000',
  '#00FF00',
  '#0000FF',
  '#FFFF00',
  '#FF00FF',
  '#00FFFF',
  '#FFA500',
  '#800080',
  '#FFC0CB',
  '#A52A2A',
  '#008000',
  '#000080',
];

const COLOR_SWATCH_CLASS: Record<string, string> = {
  '#FFFFFF': 'csp-wave1-color-swatch-white',
  '#000000': 'csp-wave1-swatch-black',
  '#333333': 'csp-wave1-swatch-gray-800',
  '#666666': 'csp-wave1-swatch-gray-600',
  '#999999': 'csp-wave1-swatch-gray-500',
  '#FF0000': 'csp-wave1-swatch-red',
  '#00FF00': 'csp-wave1-swatch-green',
  '#0000FF': 'csp-wave1-swatch-blue',
  '#FFFF00': 'csp-wave1-swatch-yellow',
  '#FF00FF': 'csp-wave1-swatch-magenta',
  '#00FFFF': 'csp-wave1-swatch-cyan',
  '#FFA500': 'csp-wave1-swatch-orange',
  '#800080': 'csp-wave1-swatch-purple',
  '#FFC0CB': 'csp-wave1-swatch-pink',
  '#A52A2A': 'csp-wave1-swatch-brown',
  '#008000': 'csp-wave1-swatch-dark-green',
  '#000080': 'csp-wave1-swatch-dark-blue',
};

interface Props {
  value: string;
  onChange: (html: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  compact?: boolean;
}

const RichTextCommentEditor: React.FC<Props> = ({
  value,
  onChange,
  ariaLabel,
  disabled = false,
  compact = false,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [formatState, setFormatState] = useState({ bold: false, italic: false, underline: false });

  useEffect(() => {
    if (!editorRef.current) return;
    if (value === '' && editorRef.current.innerHTML !== '') {
      editorRef.current.innerHTML = '';
    }
  }, [value]);

  const syncFormatState = () => {
    if (!editorRef.current) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const anchor = selection.anchorNode;
    if (anchor && !editorRef.current.contains(anchor)) return;
    try {
      setFormatState({
        bold: !!document.queryCommandState('bold'),
        italic: !!document.queryCommandState('italic'),
        underline: !!document.queryCommandState('underline'),
      });
    } catch {
      // ignore unsupported browser commands
    }
  };

  const emitChange = () => {
    if (!editorRef.current) return;
    onChange(stripBidiControlChars(editorRef.current.innerHTML));
    syncFormatState();
  };

  const handleFormat = (command: string, commandValue?: string) => {
    document.execCommand(command, false, commandValue);
    editorRef.current?.focus();
    emitChange();
  };

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    if (!editorRef.current) return;
    applyColorClassToSelection(editorRef.current, color);
    editorRef.current.focus();
    emitChange();
  };

  const handleClearFormat = () => {
    if (!editorRef.current) return;
    const text = stripBidiControlChars(editorRef.current.innerText || editorRef.current.textContent || '');
    editorRef.current.innerHTML = text;
    setFormatState({ bold: false, italic: false, underline: false });
    emitChange();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!(event.ctrlKey || event.metaKey)) return;
    const key = event.key.toLowerCase();
    if (key === 'b') {
      event.preventDefault();
      handleFormat('bold');
    } else if (key === 'i') {
      event.preventDefault();
      handleFormat('italic');
    } else if (key === 'u') {
      event.preventDefault();
      handleFormat('underline');
    }
  };

  return (
    <div>
      <div className="border rounded-top p-2 d-flex align-items-center gap-2 flex-wrap csp-wave1-toolbar csp-wave1-toolbar-theme">
        <Button
          variant={formatState.bold ? 'primary' : 'outline-secondary'}
          size="sm"
          disabled={disabled}
          onClick={() => handleFormat('bold')}
          title="Negrito (Ctrl+B)"
          aria-pressed={formatState.bold}
        >
          <strong>B</strong>
        </Button>
        <Button
          variant={formatState.italic ? 'primary' : 'outline-secondary'}
          size="sm"
          disabled={disabled}
          onClick={() => handleFormat('italic')}
          title="Itálico (Ctrl+I)"
          aria-pressed={formatState.italic}
        >
          <em>I</em>
        </Button>
        <Button
          variant={formatState.underline ? 'primary' : 'outline-secondary'}
          size="sm"
          disabled={disabled}
          onClick={() => handleFormat('underline')}
          title="Sublinhado (Ctrl+U)"
          aria-pressed={formatState.underline}
        >
          <u>U</u>
        </Button>
        <div className="vr csp-wave1-separator" />
        <div className="d-flex align-items-center gap-1">
          <Form.Label className="mb-0 small">Cor:</Form.Label>
          <Form.Control
            type="color"
            value={selectedColor}
            disabled={disabled}
            onChange={(event) => handleColorChange(event.target.value)}
            className="csp-wave1-color-picker"
            title="Selecionar cor do texto"
          />
        </div>
        <div className="d-flex gap-1">
          {COLORS.slice(0, 8).map((color) => (
            <button
              key={color}
              type="button"
              disabled={disabled}
              className={`btn btn-sm border csp-wave1-color-swatch ${COLOR_SWATCH_CLASS[color] || ''}`}
              onClick={() => handleColorChange(color)}
              title={`Cor ${color}`}
            />
          ))}
        </div>
        <div className="vr csp-wave1-separator" />
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={disabled}
          onClick={handleClearFormat}
          title="Limpar formatação"
        >
          Limpar
        </Button>
      </div>
      <div
        ref={editorRef}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        dir="ltr"
        contentEditable={!disabled}
        onKeyDown={handleKeyDown}
        onInput={emitChange}
        onBlur={emitChange}
        onPaste={(event) => {
          event.preventDefault();
          const text = stripBidiControlChars(event.clipboardData.getData('text/plain'));
          document.execCommand('insertText', false, text);
          emitChange();
        }}
        className={`border rounded-bottom p-2 csp-wave1-editor csp-wave1-editor-theme${compact ? ' csp-wave1-editor-compact' : ''}`}
        suppressContentEditableWarning
      />
      <Form.Text className="text-muted">
        Use os botões acima para formatar o texto. Suporte a negrito, itálico, sublinhado e cores.
      </Form.Text>
    </div>
  );
};

export default RichTextCommentEditor;
